import React, { Component } from "react";
import { css } from "emotion";
import { BACKGROUND, PRIMARY_TEXT } from "../../constants";
import { Button, Container, Graph, Text } from "../../components";
import { PDFLink } from "../../pdf";
import getResults, { V3_RELEASE_DATE } from "../../results";
import moment from "moment";

const COVID_RELEASE_DATE = new Date(2020, 9, 2).getTime();

const Result = ({
  title,
  description,
  averages,
  score,
  format,
  extra,
  labels,
  benchmark,
  indicator,
}) => {
  return (
    <div
      className={css`
        width: 100%;
        display: flex;
        align-items: stretch;
        background-color: white;
        // box-shadow: 0 10px 20px 0 ${BACKGROUND};
        border: 1px solid #b1b1b1;
        border-radius: 10px;
        @media (max-width: 800px) {
          flex-direction: column;
        }
      `}
    >
      <figcaption
        className={css`
          flex-grow: 1;
          flex-shrink: 1;
          min-width: 1px;
          padding: 30px 30px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          @media (max-width: 500px) {
            padding: 20px;
          }
        `}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          {["green", "yellow", "red"].includes(indicator) ? (
            <img
              src={`/icons/${indicator}.svg`}
              style={{ width: 32, height: 32, marginRight: 10 }}
            />
          ) : null}
          <Text.H3>{title}</Text.H3>
        </div>
        <br />
        <Text
          serif
          leading="copy"
          style={{
            color: "#616161",
          }}
        >
          {description}
        </Text>
      </figcaption>
      <figure
        className={css`
          flex-shrink: 0;
          max-width: 250px;
          display: flex;
          text-align: center;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          margin: 20px;
          padding: 20px 40px;
          @media (max-width: 500px) {
            padding: 20px;
          }
          @media (max-width: 800px) {
            margin: 0;
            padding-top: 0;
          }
          @media (min-width: 801px) {
            width: 350px;
            border-left: 1px solid rgba(0, 0, 0, 0.1);
          }
        `}
      >
        {averages ? (
          <Graph
            top={averages.t10}
            bottom={averages.b10}
            avg={averages.avg}
            score={score}
            format={format}
            labels={labels}
          />
        ) : (
          <Text.H1>{format(score)}</Text.H1>
        )}
        {extra && <Text.Large>{extra}</Text.Large>}
        {benchmark && (
          <Text.Small
            serif
            style={{
              color: "#616161",
            }}
          >
            benchmark: {benchmark}
          </Text.Small>
        )}
      </figure>
    </div>
  );
};

class DashboardResults extends Component {
  state = {
    selectedCampus: null,
  };

  get results() {
    return getResults(this.props.timeSubmitted).filter((r) => {
      if (this.state.selectedCampus) {
        if (r.category === "finances") return false;
      } else if (this.props.responses.campuses) {
        if (r.category === "facilities") return false;
      }
      if (r.if) return r.if(this.props.responses, this.state.selectedCampus);
      return true;
    });
  }

  get campuses() {
    return this.props.responses.campuses;
  }

  render() {
    let averages = {};
    /* no longer using averages after covid release */
    if (moment(this.props.timeSubmitted).isBefore(COVID_RELEASE_DATE)) {
      averages = this.props.averages;
    }

    return (
      <div style={{ backgroundColor: "white" }}>
        <Container style={{ margin: 0 }}>
          <header
            className={css`
              display: flex;
              align-items: baseline;
              justify-content: space-between;
              padding-top: 40px;
              @media (max-width: 1000px) {
                justify-content: center;
              }
              select {
                font-size: inherit;
                font-weight: bold;
                color: ${PRIMARY_TEXT};
              }
            `}
          >
            {this.campuses ? (
              <Text.H3
                className={css`
                  @media (max-width: 1000px) {
                    font-size: 1.25rem;
                  }
                `}
              >
                {"Your "}
                <select
                  value={this.state.selectedCampus || ""}
                  onChange={(e) => {
                    this.setState({ selectedCampus: e.target.value || null });
                  }}
                >
                  <option value="">Overall</option>
                  {this.campuses.map((name, index) => (
                    <option key={index} value={index}>
                      {name}
                    </option>
                  ))}
                </select>
                {" Report"}
              </Text.H3>
            ) : (
              <div>
                <Text.H1>Your Report</Text.H1>
                <Text italic serif style={{ color: "#B1B1B1", marginTop: 5 }}>
                  {moment(this.props.timeSubmitted).format("MMMM Do, YYYY")}
                </Text>
              </div>
            )}
            <div
              className={css`
                @media (max-width: 1000px) {
                  position: absolute;
                  top: 25px;
                  right: 25px;
                }
                @media (max-width: 400px) {
                  right: 10px;
                }
              `}
            >
              {this.props.responses && (
                <PDFLink
                  key={this.props.timeSubmitted}
                  logo={require("../../assets/images/logo.png")}
                  results={getResults(this.props.timeSubmitted)}
                  church={this.props.church}
                  averages={averages}
                  responses={this.props.responses}
                  timeSubmitted={this.props.timeSubmitted}
                  className={Button.getClassName({ kind: "outline" })}
                />
              )}
            </div>
          </header>
          {moment(this.props.timeSubmitted).isAfter(V3_RELEASE_DATE) ? (
            <ResultsListV3
              averages={averages}
              results={this.results}
              responses={this.props.responses}
              selectedCampus={this.state.selectedCampus}
            />
          ) : (
            <ResultsList
              averages={averages}
              results={this.results}
              responses={this.props.responses}
              selectedCampus={this.state.selectedCampus}
            />
          )}
          {this.props.church.customCTA ? (
            <div
              className={css`
                padding: 30vh 0;
              `}
            >
              <Text.H1>Next Steps</Text.H1>
              <br />
              <Text.Large measure leading="copy">
                {this.props.church.customCTA}
              </Text.Large>
            </div>
          ) : (
            <div
              className={css`
                padding: 30vh 0;
              `}
            >
              <Text.H1>Next Steps</Text.H1>
              <br />
              <Text.Large measure leading="copy">
                The purpose of this assessment is simple: to give you a current
                snapshot of your ministry’s health. But metrics are just a
                starting point. Let's turn the knowledge to action.
              </Text.Large>
              <br />
              <Text.Large measure leading="copy">
                If you’re not already engaged in a relationship with our team,
                learn how The Unstuck Group helps churches leverage this data
                into next steps at{" "}
                <a target="_blank" href="https://theunstuckgroup.com/">
                  https://theunstuckgroup.com/
                </a>
                .
              </Text.Large>
            </div>
          )}
        </Container>
      </div>
    );
  }
}

export default DashboardResults;

const ResultsListV3 = ({ results, averages, responses, selectedCampus }) => {
  let currentCategory = "";
  return (
    <ul
      className={css`
        margin: 0;
        padding: 0;
        list-style: none;
      `}
    >
      {results.map((result) => {
        const newCategory = result.category !== currentCategory;
        currentCategory = result.category;
        const categoryLabels = {
          reach: "Ministry Reach",
          finances: "Ministry Finance/Facilities",
          "ministry-staffing": "Ministry Staffing and Leadership",
          connection: "Ministry Connection",
        };
        const categoryIcons = {
          reach: "/icons/reach.svg",
          finances: "/icons/finances.svg",
          "ministry-staffing": "/icons/ministry-staffing.svg",
          connection: "/icons/connection.svg",
        };
        return (
          <div>
            {newCategory && (
              <div>
                <div style={{ height: 20 }} />

                <div style={{ height: 40 }} />

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: -30,
                  }}
                >
                  <img
                    src={categoryIcons[result.category]}
                    style={{
                      width: 36,
                      height: 36,
                      marginRight: 10,
                    }}
                  />
                  <h2>{categoryLabels[result.category] || ""}</h2>
                </div>
              </div>
            )}
            <li
              key={result.label}
              className={css`
                margin: 40px 0;
              `}
            >
              <Result
                averages={averages[result.id]}
                title={result.label}
                format={result.format}
                labels={result.labels}
                benchmark={result.benchmark}
                indicator={result.getIndicator(
                  result.calculate(responses, selectedCampus)
                )}
                score={result.calculate(responses, selectedCampus)}
                extra={
                  result.extra
                    ? result.extra(result.calculate(responses, selectedCampus))
                    : null
                }
                figure={result.format(
                  result.calculate(responses, selectedCampus)
                )}
                description={result.messaging(
                  result.calculate(responses, selectedCampus)
                )}
              />
            </li>
          </div>
        );
      })}
    </ul>
  );
};

const ResultsList = ({ results, averages, responses, selectedCampus }) => {
  return (
    <ul
      className={css`
        margin: 0;
        padding: 0;
        list-style: none;
      `}
    >
      {results.map((result) => (
        <li
          key={result.label}
          className={css`
            margin: 40px 0;
          `}
        >
          <Result
            averages={averages[result.id]}
            title={result.label}
            format={result.format}
            labels={result.labels}
            benchmark={result.benchmark}
            score={result.calculate(responses, selectedCampus)}
            extra={
              result.extra
                ? result.extra(result.calculate(responses, selectedCampus))
                : null
            }
            figure={result.format(result.calculate(responses, selectedCampus))}
            description={result.messaging(
              result.calculate(responses, selectedCampus)
            )}
          />
        </li>
      ))}
    </ul>
  );
};



// WEBPACK FOOTER //
// ./src/screens/dash/DashboardResults.js